import { EMPLOYEE_PERMISSION_MODULES, normalizeModuleKeyForDb, normalizeModuleKeyFromDb } from '@shared/employeePermissions';
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type { EmployeeRecord, EmployeePermission } from '../domain/employeeTypes';
import type { BranchPermission, ModuleKey } from '../../shell/session/store/sessionTypes';
import { clearBootstrapSessionCache } from '../../shell/session/bootstrapMobileSessionService';
import { teamAccessError } from './mapTeamAccessError';

/**
 * After owner mutates EBP, clear local bootstrap cache so the next fetch is fresh.
 * Employee devices: EmployeePermissionsFreshnessProvider polls permissions_updated_at
 * (60s + AppState) and force-reboots session → soft-block if revoked.
 */
function invalidatePermissionsCaches(): void {
  clearBootstrapSessionCache();
}

type EmployeeRow = {
  id: string;
  auth_user_id: string;
  full_name: string | null;
  role: string | null;
  invited_by: string;
  created_at: string;
  is_active: boolean | null;
  earns_commission: boolean | null;
  commission_plan_id: string | null;
};

type PermissionRow = {
  employee_id: string;
  branch_id: string;
  module: string;
  can_view: boolean;
  can_edit: boolean;
  can_void: boolean;
  can_discount: boolean;
  can_manage_agenda: boolean;
  can_view_others: boolean;
  actions: Record<string, boolean> | null;
  revoked_at: string | null;
};

/** Canonical module list — shared/employeePermissions, with 'Fiado' → 'Customers' for app-side use. */
export const MODULES: ModuleKey[] = EMPLOYEE_PERMISSION_MODULES.map(
  (m) => normalizeModuleKeyFromDb(m) as ModuleKey
);

function parseActions(raw: unknown): Record<string, boolean> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === true || value === 'true') out[key] = true;
    else if (value === false || value === 'false') out[key] = false;
  }
  return out;
}

export async function fetchEmployees(): Promise<EmployeeRecord[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) return [];

  // RLS ve membresías de TODAS las orgs del usuario (invited_by, via_team,
  // owner rows de orgs donde es miembro). Scope a la org activa para que
  // un usuario multi-org no vea mezcladas las listas ni la row owner de
  // otra org. Exclude self (Team = invitados).
  const { getSessionOrganizationId } = await import('../../shell/session/sessionOrganization');
  const orgId = await getSessionOrganizationId();

  let query = supabase
    .from('employees')
    .select('id, auth_user_id, full_name, role, invited_by, created_at, is_active, earns_commission, commission_plan_id, organization_id')
    .neq('auth_user_id', user.id)
    .order('created_at', { ascending: true });
  if (orgId) query = query.eq('organization_id', orgId);

  const { data: employees, error: empError } = await query;

  if (empError || !employees?.length) return [];

  const ids = (employees as EmployeeRow[]).map((e) => e.id);
  const { data: perms, error: permsError } = await supabase
    .from('employee_branch_permissions')
    .select(
      'employee_id, branch_id, module, can_view, can_edit, can_void, can_discount, can_manage_agenda, can_view_others, actions, revoked_at'
    )
    .in('employee_id', ids)
    .is('revoked_at', null);

  if (permsError) return [];

  const permsByEmployee = new Map<string, PermissionRow[]>();
  (perms ?? []).forEach((p: PermissionRow) => {
    const list = permsByEmployee.get(p.employee_id) ?? [];
    list.push(p);
    permsByEmployee.set(p.employee_id, list);
  });

  const uniqueBranchIds = [
    ...new Set((perms ?? []).map((p: PermissionRow) => p.branch_id).filter(Boolean)),
  ];
  const branchNameById: Record<string, string> = {};
  if (uniqueBranchIds.length > 0) {
    const { data: branchRows } = await supabase
      .from('branches')
      .select('id, name')
      .in('id', uniqueBranchIds);
    for (const row of branchRows ?? []) {
      const id = row?.id != null ? String(row.id) : '';
      const name = row?.name != null ? String(row.name).trim() : '';
      if (id && name) branchNameById[id] = name;
    }
  }

  const byBranch = (rows: PermissionRow[]): Map<string, BranchPermission[]> => {
    const map = new Map<string, BranchPermission[]>();
    rows.forEach((r) => {
      const list = map.get(r.branch_id) ?? [];
      // Fase C: normalize 'Fiado' → 'Customers' when reading from DB
      const normalizedModule = normalizeModuleKeyFromDb(r.module) as ModuleKey;
      if (MODULES.includes(normalizedModule)) {
        list.push({
          module: normalizedModule,
          canView: r.can_view,
          canEdit: r.can_edit,
          canVoid: r.can_void,
          canDiscount: r.module === 'POS' ? r.can_discount : undefined,
          canManageAgenda: r.module === 'Agenda' ? r.can_manage_agenda : undefined,
          canViewOthers: r.can_view_others ?? true,
          actions: parseActions(r.actions),
        });
      }
      map.set(r.branch_id, list);
    });
    map.forEach((arr, branchId) => {
      const hasModule = new Set(arr.map((a) => a.module));
      MODULES.forEach((m) => {
        if (!hasModule.has(m)) {
          arr.push({
            module: m,
            canView: false,
            canEdit: false,
            canVoid: false,
            canDiscount: m === 'POS' ? false : undefined,
            canViewOthers: true,
            actions: {},
          });
        }
      });
      arr.sort((a, b) => MODULES.indexOf(a.module) - MODULES.indexOf(b.module));
    });
    return map;
  };

  const authUserIds = (employees as EmployeeRow[])
    .map((e) => e.auth_user_id)
    .filter(Boolean);
  const emailByAuthUserId = new Map<string, string>();
  if (authUserIds.length > 0) {
    const { data: invites } = await supabase
      .from('employee_invitations')
      .select('auth_user_id, email')
      .in('auth_user_id', authUserIds)
      .eq('status', 'accepted');
    (invites ?? []).forEach((row: any) => {
      if (row.auth_user_id && row.email) {
        emailByAuthUserId.set(row.auth_user_id, row.email);
      }
    });
  }

  return (employees as EmployeeRow[]).map((emp): EmployeeRecord => {
    const permRows = permsByEmployee.get(emp.id) ?? [];
    const branchMap = byBranch(permRows);
    const branchAssignments: EmployeePermission[] = Array.from(branchMap.entries()).map(([branchId, permissions]) => ({
      branchId,
      branchName: branchNameById[branchId],
      permissions,
    }));
    const isOwner = emp.role === 'owner';
    return {
      id: emp.id,
      authUserId: emp.auth_user_id,
      name: emp.full_name ?? emp.auth_user_id.slice(0, 8),
      role: isOwner ? 'Dueño' : emp.role ?? 'Empleado',
      isOwner,
      email: emailByAuthUserId.get(emp.auth_user_id),
      createdAt: emp.created_at,
      isActive: emp.is_active !== false,
      earnsCommission: emp.earns_commission === true,
      commissionPlanId: emp.commission_plan_id ?? null,
      branchAssignments,
    };
  });
}

/**
 * Membresía owner del usuario actual en la org activa (role='owner').
 * `fetchEmployees` excluye self a propósito (Team = empleados invitados);
 * esta consulta trae la row propia para pickers de atribución (POS "quién
 * vendió", caja, delivery, reportes) donde el dueño sí es opción.
 */
export async function fetchSelfOwnerMembership(): Promise<EmployeeRecord | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) return null;
  const { getSessionOrganizationId } = await import('../../shell/session/sessionOrganization');
  const orgId = await getSessionOrganizationId();
  if (!orgId) return null;
  const { data, error } = await supabase
    .from('employees')
    .select('id, auth_user_id, full_name, role, invited_by, created_at, is_active')
    .eq('auth_user_id', user.id)
    .eq('organization_id', orgId)
    .eq('role', 'owner')
    .eq('is_active', true)
    .maybeSingle();
  if (error || !data) return null;
  const emp = data as EmployeeRow;
  return {
    id: emp.id,
    authUserId: emp.auth_user_id,
    name: emp.full_name?.trim() || 'Vos',
    role: 'Dueño',
    isOwner: true,
    createdAt: emp.created_at,
    isActive: emp.is_active !== false,
    branchAssignments: [],
  };
}

type EbpModulePayload = {
  module: string;
  canView: boolean;
  canEdit: boolean;
  canVoid: boolean;
  canDiscount: boolean;
  canManageAgenda?: boolean;
  canViewOthers?: boolean;
  actions?: Record<string, boolean>;
};

function mapTeamRpcError(error: { message?: string } | null, fallback: string): Error {
  return teamAccessError(error, fallback);
}

/** Upsert EBP (edit / preset / copy) vía RPC server-authoritative (Slice 5). */
export async function setEmployeeBranchPermissions(
  employeeId: string,
  branchId: string,
  modules: EbpModulePayload[],
  options?: { auditEvent?: string; auditMeta?: Record<string, unknown> }
): Promise<void> {
  if (!modules.length) throw new Error('Faltan módulos para guardar los permisos.');
  const { data, error } = await supabase.rpc('set_employee_branch_permissions', {
    p_employee_id: employeeId,
    p_branch_id: branchId,
    p_modules: modules.map((m) => ({
      module: normalizeModuleKeyForDb(m.module),
      can_view: m.canView,
      can_edit: m.canEdit,
      can_void: m.canVoid ?? false,
      can_discount: m.canDiscount ?? false,
      can_manage_agenda: m.canManageAgenda ?? false,
      can_view_others: m.canViewOthers ?? true,
      actions: m.actions ?? {},
    })),
    p_audit_event: options?.auditEvent ?? 'employee.permission.change',
    p_audit_meta: options?.auditMeta ?? {},
  });
  if (error) {
    const fail = mapTeamRpcError(error, 'No se pudo actualizar el permiso.');
    captureOpsFailure({
      module: 'team',
      op: 'team.grant',
      error: fail,
      ingestError: fail.message,
      itemId: employeeId,
      extra: { employeeId, branchId, modules: modules.length },
    });
    throw fail;
  }
  const body = data as { ok?: boolean; error?: string } | null;
  if (body && body.ok === false) {
    throw mapTeamRpcError({ message: body.error }, 'No se pudo actualizar el permiso.');
  }
  invalidatePermissionsCaches();
}

export async function updateEmployeePermission(
  employeeId: string,
  branchId: string,
  module: ModuleKey,
  canView: boolean,
  canEdit: boolean,
  canVoid?: boolean,
  canDiscount?: boolean,
  canManageAgenda?: boolean,
  canViewOthers?: boolean,
  actions?: Record<string, boolean>,
  options?: { auditMeta?: Record<string, unknown> }
): Promise<void> {
  await setEmployeeBranchPermissions(
    employeeId,
    branchId,
    [
      {
        module,
        canView,
        canEdit,
        canVoid: canVoid ?? false,
        canDiscount: canDiscount ?? false,
        canManageAgenda,
        canViewOthers,
        actions,
      },
    ],
    {
      auditEvent: 'employee.permission.change',
      auditMeta: { module, ...(options?.auditMeta ?? {}) },
    }
  );
}

/** Permisos por defecto al agregar una sucursal (todo denegado hasta que el dueño habilite). */
export async function insertEmployeeBranchWithDefaults(
  employeeId: string,
  branchId: string,
  options?: { includeCustomerModule?: boolean; includePromotionsModule?: boolean; includeExpensesModule?: boolean; includePurchasesModule?: boolean }
): Promise<void> {
  let modules = MODULES;
  if (options?.includeCustomerModule === false) {
    modules = modules.filter((m) => m !== 'Customers');
  }
  if (options?.includePromotionsModule === false) {
    modules = modules.filter((m) => m !== 'Promotions');
  }
  if (options?.includeExpensesModule === false) {
    modules = modules.filter((m) => m !== 'Expenses');
  }
  if (options?.includePurchasesModule === false) {
    modules = modules.filter((m) => m !== 'Purchases');
  }

  const { error } = await supabase.rpc('assign_employee_branch', {
    p_employee_id: employeeId,
    p_branch_id: branchId,
    p_modules: modules.map((m) => normalizeModuleKeyForDb(m)),
  });
  if (error) throw mapTeamRpcError(error, 'No se pudo agregar la sucursal.');
  invalidatePermissionsCaches();
}

/** Revoca acceso a una sucursal (baja lógica; conserva historial de permisos). */
export async function revokeEmployeeBranchAccess(employeeId: string, branchId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_employee_branch_access', {
    p_employee_id: employeeId,
    p_branch_id: branchId,
  });
  if (error) throw mapTeamRpcError(error, 'No se pudo quitar el acceso a la sucursal.');
  invalidatePermissionsCaches();
}

/** Reactiva un empleado revocado: is_active = true y revive permisos de sucursal. */
export async function reactivateEmployeeAccess(employeeId: string): Promise<void> {
  const { error } = await supabase.rpc('reactivate_employee_access', {
    p_employee_id: employeeId,
  });
  if (error) throw mapTeamRpcError(error, 'No se pudo reactivar el acceso.');
  invalidatePermissionsCaches();
}

/** Aplica un preset/copia de permisos a un empleado + sucursal (RPC atómico). */
export async function applyPermissionPreset(
  employeeId: string,
  branchId: string,
  preset: Record<
    string,
    {
      canView: boolean;
      canEdit: boolean;
      canVoid: boolean;
      canDiscount: boolean;
      canManageAgenda?: boolean;
      canViewOthers?: boolean;
      actions?: Record<string, boolean>;
    }
  >,
  options?: { auditEvent?: string; auditMeta?: Record<string, unknown> }
): Promise<void> {
  const modules = Object.entries(preset).map(([module, perms]) => ({
    module,
    canView: perms.canView,
    canEdit: perms.canEdit,
    canVoid: perms.canVoid,
    canDiscount: perms.canDiscount,
    canManageAgenda: perms.canManageAgenda,
    canViewOthers: perms.canViewOthers,
    actions: perms.actions,
  }));
  await setEmployeeBranchPermissions(employeeId, branchId, modules, {
    auditEvent: options?.auditEvent ?? 'employee.preset.apply',
    auditMeta: options?.auditMeta ?? {},
  });
}

export async function revokeEmployeeAccess(employeeId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_employee_access', {
    p_employee_id: employeeId,
  });
  if (error) {
    const fail = mapTeamRpcError(error, 'No se pudo revocar el acceso.');
    captureOpsFailure({
      module: 'team',
      op: 'team.revoke',
      error: fail,
      ingestError: fail.message,
      itemId: employeeId,
      extra: { employeeId },
    });
    throw fail;
  }
  invalidatePermissionsCaches();
}
